import { equipmentQuote, type EquipmentKind } from '../domain/equipment.js';
import { DrawSystemImpl, System } from "minecs";
import { Container, Graphics, Rectangle, RoundedRectangle, Sprite, Text } from "pixi.js";
import type { ReadOnlyGameModel } from "yage/game/GameModel";
import { PixiViewportSystem } from "yage/systems/render/PixiViewport";
import { Transform } from "yage/schemas/entity/Transform";
import { RigidBox } from "yage/schemas/physics/RigidBox";
import { Locomotion } from "yage/schemas/entity/Locomotion";
import { AirportState, AirportStation, AirportAgent, AirportPassenger, AirportStaff, AirportZone, AirportBag, AirportPlane, AirportPath, AirportCash, AirportTrash, AirportVending, AirportPilot } from "../components/index.js";
import { gatePose, boardingWorkPoint } from "../domain/queues.js";
import { CONFIG, ADMIN, onPad, WORK_PAD, ACTION_PAD, KIOSK, stationPad, vendingPoint, VENDING, GATES, CREW, OPERATIONS, walkableAirportPoint, ANGRY_PATIENCE, SAD_RADIUS, internationalTraveller, LANDSIDE_TOP } from "../domain/config.js";
import type { PadKind } from "../domain/types.js";
import { capacity, serviceFrames, vendingReady, vendingCost, dividerCost, specialCost, SPECIAL_TIMERS, unlockCost, managerCost, upgradeCost } from "../domain/economy.js";
import { ids, state, career, stations, distance, zoneAvailable, zoneId, vending } from "../services/world.js";
import { LOCAL, localPlayer, travel, send } from "../services/local.js";
import { Gesture, STICK_REACH } from "../render/gesture.js";
import { findMagneticWorkDesk, nearestAvailableWorkDesk, isMobileControls, STICK_MAGNET_RADIUS } from "../render/magnetic.js";
import { planes } from "../services/operations.js";
import { ROUTES, PHASE, remainingTripFrames } from "../domain/operations.js";
import { AirportHUD } from "../render/hud.js";
import { AIRPORTS, airlineMode, stationAllowed, tier } from "../domain/airports.js";
import { C, shape, rounded, label, suitcase, bills, fmt, setText } from "../render/primitives.js";
import { floor, walls, hoardedFloor, drawGateFloor, stationArt, person, planeArt, SHIRTS, vendingArt, trashArt, jetBridges, airstairs, DOOR_SILL, deckDrop, deckFacade, deckCutout, behindDeck, type Standing } from "../render/art.js";
import { gatePlanKey, gateLayout, lockedExpansions, hoardingFor } from "../domain/layout.js";
import { activeLayout } from "../domain/active-layout.js";
import { ExpansionOpening, ringCentre } from "../render/opening.js";
import { gateDoor, groundPose, parkedHeading, boardingStyle, aircraftStand, aircraftDoor, stairsProgress, standReach } from "../domain/gateflow.js";
import { insidePolygon } from '../domain/geometry.js';
import { archetype } from "../domain/passengers.js";
import { activeCrises, crisisPoint } from "../services/events.js";
import { diaryLines } from "../services/diary.js";
import { shiftsActive } from "../domain/act2.js";
import { metalDetector, gateDoors, departureBoardFrames, queueRopes } from '../render/fixtures.js';
import { disclosePad, FLOOR_UPGRADE_DWELL } from '../domain/progression.js';
import { objectiveAction, guidanceTarget, guidanceCaption } from '../services/guidance.js';
import type { Guidance } from '../services/guidance.js';
import { chevronTrail } from '../domain/navigation.js';
/** One pictograph per pad kind — the pad's whole vocabulary. */
const ZONE_GLYPH: Record<string, string> = { admin: 'ADMIN', work: 'WORK', build: 'BUILD', hire: 'CREW', upgrade: 'UPGRADE', divider: 'QUEUE', belt: 'LOAD', trash: 'BIN', vending: 'UPGRADE', puzzle: 'RADIO', prestige: 'NEXT AIRPORT', pilots: 'PILOTS', routes: 'ROUTES', chairs: 'SEATS', shuttle: 'SHUTTLE', boots: 'SPEED', bags: 'CARRY', cleaning: 'CLEANING' };
import { detail, prop, faceAircraft, refreshDirectionalSprites } from "../render/sprites.js";
import { cameraQuarter, floorMatrix, projectDirection, projectWorld, rotateCamera, standAt, unprojectWorld } from '../render/camera.js';
import { waitingSeats, EXTRA_SEATS } from '../domain/gateflow.js';
import { GateBoards } from '../render/gateboards.js';
import { inquiryPoint } from '../domain/queues.js';
import { posePerson, peopleAnimationTick } from '../render/people.js';
import { EntranceDoors } from '../render/entrance.js';
import { pileLayers, stepFollow, followIdle, stepSway, type FollowState } from '../render/feel.js';
/** Desks face south: customers queue and are served on the south side, whoever works one stands behind it. */
const SEAT_LIFT = -4;
type PersonArt = ReturnType<typeof person> & {
    name: Text;
    question: Container;
    stack: Container;
    bags: number;
    litter: number;
    facingX: number;
    facingY: number;
    bubble: Container;
    sadFace: Graphics;
    angryFace: Text;
    mood: number;
    /** Presentation-only follow envelope and carry-stack spring; the sim never reads these. */
    follow: FollowState;
    sway: number;
    swayV: number;
    lastVX: number;
    lastVY: number;
    stackBaseX: number[];
};
type StationArt = ReturnType<typeof stationArt> & { posts: Container[]; postLevel: number };
/** Horizontal jitter of each bill layer in a floor pile, base layer first. */
const PILE_X = [0, 6, -6, 5, -5];
/** The way a desk's customers stand from it: its own facing (south on the original floor plan). */
function deskFacing(pose: { facing?: readonly number[] } | undefined) { return { x: pose?.facing?.[0] ?? 0, y: pose?.facing?.[1] ?? 1 }; }
/** Every desk on the floor with its work pad: the departments, then each Boarding gate's own desk. */
function desks(code: string) {
    const gates = gateLayout(code).GATES.map((_, gate) => ({ stage: 6, gate, pose: gatePose(code, gate), pad: boardingWorkPoint(gate, code) }));
    return [...CONFIG.stages.map((pose, stage) => ({ stage, gate: -1, pose, pad: stationPad(stage, 'work') })).filter(d => d.stage !== 6), ...gates];
}
function restingFacing(at: { x: number; y: number }, passenger: AirportPassenger | null, staff: AirportStaff | null, captain: boolean, code: string) {
    if (passenger && passenger.vending >= 0 && VENDING[passenger.vending]) {
        const machine = VENDING[passenger.vending], spot = vendingPoint(passenger.vending);
        if (distance(at, spot) < 40) return { x: machine.x - spot.x, y: machine.y - spot.y };
    }
    // Travellers face the counter they are queuing for; whoever works a desk faces its customers.
    if (passenger && CONFIG.stages[passenger.stage]) {
        const f = deskFacing(passenger.stage === 6 ? gatePose(code, Math.max(0, passenger.gate)) : CONFIG.stages[passenger.stage]);
        return { x: -f.x, y: -f.y };
    }
    if (!captain && !(staff && ['clerk', 'crew'].includes(staff.role) && CONFIG.stages[staff.stage])) return null;
    const worked = desks(code).filter(d => distance(at, d.pad) < 45).sort((x, y) => distance(at, x.pad) - distance(at, y.pad))[0];
    if (worked) return deskFacing(worked.pose);
    if (!staff) return null;
    const own = desks(code).filter(d => d.stage === staff.stage).sort((x, y) => distance(at, x.pad) - distance(at, y.pad))[0];
    return deskFacing(own?.pose);
}

/** Draw frames of standing still before the goal glow appears (about 4 s). */
const IDLE_CUE_FRAMES = 240;
@System(AirportState)
export class AirportScene extends DrawSystemImpl<ReadOnlyGameModel> {
    static depth = 11500;
    world: Container | null = null;
    /** Flat floor layer: drawn in world coordinates under the isometric floor matrix. */
    private ground: Container | null = null;
    private entrance: EntranceDoors | null = null;
    /** Upright things placed once on a world point and re-projected when the camera turns. */
    private standing: Standing[] = [];
    private wallPieces: Container[] = [];
    private detectors: Container[] = [];
    private scanners: Container[] = [];
    private boardGateCount = -1;
    hud: AirportHUD | null = null;
    private gateDesks = new Map<number, ReturnType<typeof stationArt>>();
    private scenery = new Map<number, StationArt>();
    /** Unlock transitions seen at least once, so the pop-in reveal never fires on world load. */
    private revealed = new Map<number, boolean>();
    private reveals: { art: StationArt; flash: Graphics; at: number }[] = [];
    private people = new Map<number, PersonArt>();
    private bubbleBudget = new Map<number, number>();
    private zones = new Map<number, {
        root: Container;
        pad: Container;
        circle: Graphics;
        title: Text;
        price: Text;
        icon: Container;
    }>();
    private parcels = new Map<number, Container>();
    private planes = new Map<number, ReturnType<typeof planeArt>>();
    /** Airstairs wheeled up to each parked aircraft at a stairs airport, keyed by plane entity. */
    private airstairs = new Map<number, Container>();
    private airfield: Container | null = null;
    private facade: Graphics | null = null;
    /** Airfield things behind an upper deck: drawn just above the floor, clipped off the deck, under its walls. */
    private behind: Container | null = null;
    private planeTrail = new Map<number, { x: number; y: number }>();
    private gateBoards: GateBoards | null = null;
    private cash = new Map<number, {
        root: Container;
        pile: Container;
        chip: Text;
        bracket: number;
    }>();
    private chairs: Container[] = [];
    private chairLevel = -1;
    private marker: Graphics | null = null;
    private arrows: Graphics | null = null;
    private tutorialCue: Text | null = null;
    private objective: Guidance | null = null;
    private collision: Graphics | null = null;
    private litter = new Map<number, { root: Container; stink: Graphics }>();
    private machines = new Map<number, ReturnType<typeof vendingArt>>();
    private fence: Graphics | null = null;
    private floorClip: Graphics | null = null;
    private fenceText: Text | null = null;
    private fenceTier = '';
    private lastOffline = -1;
    private viewQuarter = -1;
    private onKey = (event: KeyboardEvent) => {
        if (LOCAL.modalOpen || event.repeat || event.altKey || event.ctrlKey || event.metaKey) return;
        const key = event.key.toLowerCase(), g = LOCAL.model, act2 = !!g && shiftsActive(state(g));
        if (key === 'q') rotateCamera(-1);
        else if (key === 'e') rotateCamera(1);
        // TX-024 hotkeys: Menu, Plan, Report and Inbox.
        else if (key === 'm') this.hud?.modal.open('settings');
        else if (key === 'p') this.hud?.modal.open('plan');
        else if (key === 'r' && act2) this.hud?.modal.open('report');
        else if (key === 'i' && act2) this.hud?.modal.open('inbox');
        else return;
        event.preventDefault();
    };
    private flightSigns: { node: Container; kind: 'pilots' | 'routes' }[] = [];
    private builtLayout = '';
    private initScene(g: ReadOnlyGameModel) {
        const engine = g.getSystem(PixiViewportSystem), vp = engine.viewport;
        if (!vp)
            return false;
        this.builtLayout = activeLayout();
        this.world = new Container();
        this.world.sortableChildren = true;
        vp.addChild(this.world);
        const art = floor(point => {
            if (LOCAL.modalOpen) return;
            if (point.x === ADMIN.x && point.y === ADMIN.y) {
                if (disclosePad(state(g), stations(g), 'admin', -1)) this.visitZone(zoneId(g, 'admin'));
            } else send('move', point);
        });
        this.ground = art.ground;
        this.airfield = art.airfield;
        this.gateFloor = art.gates;
        // The terminal floor is clipped to the current airport's outline; the mask lives in the same floor space.
        this.floorClip = shape(art.ground);
        art.inside.mask = this.floorClip;
        this.floorInside = art.inside;
        this.world.addChild(this.ground);
        for (const item of art.props) this.stand(item.node, item.x, item.y, item.depth);
        this.entrance = new EntranceDoors();
        this.world.addChild(this.entrance.root);
        this.marker = shape(this.ground);
        this.arrows = shape(this.ground);
        this.tutorialCue = label(this.world, '', 0, 0, 15, C.teal, '800');
        this.tutorialCue.anchor.set(0.5, 1);
        Object.assign(this.tutorialCue.style, { stroke: C.white, strokeThickness: 5, lineJoin: 'round' });
        this.tutorialCue.eventMode = 'none';
        this.collision = shape(this.ground);
        this.gateBoards = new GateBoards();

        vp.eventMode = 'static';
        const radius = CONFIG.width + CONFIG.height;
        vp.hitArea = new Rectangle(CONFIG.width / 2 - radius, CONFIG.height / 2 - radius, radius * 2, radius * 2);
        vp.cursor = 'crosshair';
        vp.on('pointerdown', this.onDown);
        vp.on('pointermove', this.onMove);
        vp.on('pointerup', this.onUp);
        vp.on('pointerupoutside', this.onUp);
        vp.on('pointercancel', this.onCancel);
        this.stickArt = new Graphics();this.stickArt.eventMode = 'none';this.stickArt.zIndex = 950;engine.pixiApp.stage.addChild(this.stickArt);
        window.addEventListener('keydown', this.onKey);
        this.fence = shape(this.ground);
        this.fenceText = label(new Container(), 'ROOM TO GROW\nThis land opens when you\nmove to a bigger airport.', 0, 0, 20, 0x3f6f62, '800');
        this.fenceText.anchor.set(0.5, 1);
        this.world.addChild(this.fenceText.parent!);
        for (const [x, y, w, text] of [[CREW.x, CREW.y, 124, 'PILOT OFFICE'], [OPERATIONS.x, OPERATIONS.y, 144, 'FLIGHT PLANNING']] as const) {
            const sign = new Container();
            rounded(sign, -w / 2, -64, w, 26, 8, C.navy);
            label(sign, text, 0, -51, 11, C.white, '800').anchor.set(0.5);
            this.stand(sign, x, y, 7);
            this.flightSigns.push({node:sign,kind:x === CREW.x ? 'pilots' : 'routes'});
        }
        this.hud = new AirportHUD(engine.pixiApp.stage);
        return true;
    }
    private stand(node: Container, x: number, y: number, depth = 0) {
        this.standing.push({ node, x, y, depth });
        this.world!.addChild(node);
        standAt(node, x, y, depth);
    }
    /** Lays the floor and every upright prop out for the current camera quarter. */
    private turnView(gates: number, airport: string) {
        this.ground!.transform.setFromMatrix(floorMatrix());
        // The airfield sits a storey down (straight down the screen) below an upper departures deck.
        const top = unprojectWorld(0, 0), below = unprojectWorld(0, deckDrop(airport));
        this.airfield!.position.set(below.x - top.x, below.y - top.y);
        this.facade?.destroy();
        const outline = gateLayout(airport).OUTLINE;
        this.facade = deckFacade(outline ?? [], outline ? deckDrop(airport) : 0);
        this.world!.addChild(this.facade);
        if (!this.behind) { this.behind = new Container(); this.behind.sortableChildren = true; this.behind.zIndex = -9998; this.world!.addChild(this.behind); }
        (this.behind.mask as Graphics | null)?.destroy();
        const cut = outline && deckDrop(airport) ? deckCutout(outline) : null;
        if (cut) this.world!.addChild(cut);
        this.behind.mask = cut;
        for (const item of this.standing) standAt(item.node, item.x, item.y, item.depth);
        this.wallPieces.forEach(piece => piece.destroy({ children: true }));
        const locked = lockedExpansions(airport, gates);
        this.wallPieces = walls(gateLayout(airport).BOUNDARIES, gateLayout(airport).DIVIDER, gateLayout(airport).WALL_RUNS, hoardingFor(airport, gates), outline);
        this.hoarded?.destroy({ children: true });
        this.hoarded = hoardedFloor(locked.map(x => { const gate = gateLayout(airport).GATES[x.opensWith] as { id?: string | null; door?: { x: number; y: number } };
            return { ring: x.ring, gate: gate.id ?? String.fromCharCode(65 + x.opensWith), door: gate.door ?? { x: 0, y: 0 } }; }));
        this.floorInside!.addChild(this.hoarded);
        this.world!.addChild(...this.wallPieces);
        this.detectors.forEach(node => node.destroy({ children: true }));
        this.scanners = metalDetector();
        this.detectors = [...this.scanners, ...gateDoors(gates, gateLayout(airport).GATES), ...departureBoardFrames(gates), ...(boardingStyle(airport) === 'bridge' ? jetBridges(airport, gates) : [])];
        for (const node of this.detectors) {
            const apron = (node as Container & { apron?: { x: number; y: number } }).apron;
            (apron && outline && deckDrop(airport) && behindDeck(outline, apron.x, apron.y) ? this.behind! : this.world!).addChild(node);
        }
        refreshDirectionalSprites();
        this.fenceTier = '';
        this.chairLevel = -1;
    }
    /** Hoarding a gate purchase took down falls away; a camera glance shows an area that opened out of view. */
    private openExpansions(g: ReadOnlyGameModel, airport: string, before: number, after: number, captain: number) {
        const standing = new Set(hoardingFor(airport, after).map(r => `${r.x},${r.y},${r.w},${r.h}`));
        const removed = hoardingFor(airport, before).filter(r => !standing.has(`${r.x},${r.y},${r.w},${r.h}`));
        const still = new Set(lockedExpansions(airport, after).map(x => x.ring));
        const opened = lockedExpansions(airport, before).filter(x => !still.has(x.ring));
        if (!removed.length && !opened.length) return;
        const GATES = gateLayout(airport).GATES as readonly { id?: string | null; door?: { x: number; y: number } }[], door = GATES[after - 1]?.door ?? { x: 0, y: 0 };
        this.opening ??= new ExpansionOpening(this.world!, this.floorInside!);
        this.opening.start(g.frame, removed, opened.map(x => ({ ring: x.ring, gate: GATES[x.opensWith]?.id ?? String.fromCharCode(65 + x.opensWith), door: GATES[x.opensWith]?.door ?? door })), door, LOCAL.reducedMotion);
        // Only when most of the new floor is off screen, and only while the captain stands still (moving hands the view back).
        const vp = g.getSystem(PixiViewportSystem).viewport, view = vp?.getVisibleBounds();
        const centre = opened.length ? ringCentre(opened[0].ring) : null, seen = centre && view ? projectWorld(centre.x, centre.y) : null;
        if (!LOCAL.reducedMotion && captain >= 0 && seen && view && !view.contains(seen.x, seen.y - 60)) {
            const at = g.getTypedUnsafe(Transform, captain);
            this.openingFocus = LOCAL.guidanceFocus = { point: centre!, captain, origin: { x: at.x, y: at.y } };
        } else this.openingFocus = null;
    }
    private opening: ExpansionOpening | null = null;
    private openingFocus: typeof LOCAL.guidanceFocus = null;
    private visitZone(id: number) {
        const g = LOCAL.model, captain = g ? localPlayer(g) : -1;
        if (!g || captain < 0 || LOCAL.modalOpen || id < 0 || !zoneAvailable(g, id)) return;
        const z = g.getTypedUnsafe(AirportZone, id), p = g.getTypedUnsafe(Transform, id);
        const at = g.getTypedUnsafe(Transform, captain), distanceToPad = distance(at, p);
        if (z.kind === 'prestige' && distanceToPad < 90) { this.hud!.modal.open('prestige'); return; }
        if (z.kind === 'puzzle' && distanceToPad < 115) { this.hud!.modal.open('puzzle'); return; }
        if (['pilots', 'routes'].includes(z.kind) && distanceToPad < 72) { this.hud!.modal.open(z.kind); return; }
        if (z.kind === 'admin' && distanceToPad < 60) { this.hud!.modal.open('admin'); return; }
        if (['upgrade', 'hire', 'build', 'vending'].includes(z.kind) && distanceToPad < 60) { this.hud!.modal.openFacility(z.stage, z.kind === 'vending'); return; }
        if ((z.kind === 'divider' || z.kind in SPECIAL_TIMERS) && onPad(z.kind, at, p)) { this.hud!.modal.openEquipment(z.kind as EquipmentKind, z.stage); return; }
        travel(id);
    }
    /** A repeated press on a nearby pad reopens its quote without another walk. */
    private lastArrivals = -1;
    private gesture = new Gesture();
    private gateFloor: Container | null = null;
    private floorInside: Container | null = null;
    private hoarded: Container | null = null;
    private plan: string | null = null;
    private idleFrames = 0;
    private idleAt = { x: 0, y: 0 };
    /** Whether the idle goal glow is showing (TX-019). */
    idleCue = false;
    /** Where the last unreachable tap landed (world point), shown until `until` (ms). */
    invalidTap: { x: number; y: number; until: number } | null = null;
    private stickArt: Graphics | null = null;
    private onDown = (event: any) => { if (!LOCAL.modalOpen && LOCAL.model) this.gesture.down(event.global.x, event.global.y, performance.now()); };
    private onMove = (event: any) => {
        const g = LOCAL.model, result = this.gesture.move(event.global.x, event.global.y, performance.now()), anchor = this.gesture.stick;
        this.drawStick(LOCAL.controls === 'tap' ? null : anchor, event.global);
        if (result.kind !== 'steer' || !g || LOCAL.modalOpen || !anchor || LOCAL.controls === 'tap') return;
        // Turn the screen-space drag into a world direction through the current (rotated, isometric) view.
        const vp = g.getSystem(PixiViewportSystem).viewport, a = vp.toWorld(anchor.x, anchor.y), b = vp.toWorld(anchor.x + result.dx * 60, anchor.y + result.dy * 60);
        const from = unprojectWorld(a.x, a.y), to = unprojectWorld(b.x, b.y), length = Math.hypot(to.x - from.x, to.y - from.y);
        const id = localPlayer(g);
        if (id < 0 || length === 0) return;
        const at = g.getTypedUnsafe(Transform, id), maxX = tier(state(g)).maxX, lx = gateLayout(tier(state(g)).code).OUTLINE;
        const dir = { x: (to.x - from.x) / length, y: (to.y - from.y) / length };
        if (isMobileControls(event, this.hud?.isSmall)) {
            const magnet = findMagneticWorkDesk(g, at, dir, STICK_MAGNET_RADIUS);
            if (magnet && walkableAirportPoint(magnet.point, maxX, lx)) {
                send('move', { x: magnet.point.x, y: magnet.point.y, zone: magnet.zone });
                return;
            }
        }
        for (const reach of [STICK_REACH, STICK_REACH / 2, STICK_REACH / 4]) {
            const target = { x: at.x + dir.x * reach, y: at.y + dir.y * reach };
            if (walkableAirportPoint(target, maxX, lx)) { send('move', target); return; }
        }
    };
    private onUp = (event: any) => {
        const result = this.gesture.up(event.global.x, event.global.y, performance.now());
        this.drawStick(null);
        if (result.kind === 'tap' && LOCAL.controls !== 'stick') this.onTap(event);
        else if (result.kind === 'release' && !LOCAL.modalOpen && LOCAL.controls !== 'tap') {
            const g = LOCAL.model, id = g ? localPlayer(g) : -1;
            if (g && id >= 0 && isMobileControls(event, this.hud?.isSmall)) {
                const at = g.getTypedUnsafe(Transform, id);
                const desk = nearestAvailableWorkDesk(g, at, STICK_MAGNET_RADIUS);
                if (desk && !onPad('work', at, desk.point)) {
                    const agent = g.getTypedUnsafe(AirportAgent, id);
                    const path = g.getTypedUnsafe(AirportPath, id);
                    const toDesk = { x: desk.point.x - at.x, y: desk.point.y - at.y };
                    const movingToDesk = path.moving && ((path.targetX - at.x) * toDesk.x + (path.targetY - at.y) * toDesk.y > 0);
                    if (agent.intent === desk.zone || movingToDesk) {
                        if (agent.intent !== desk.zone) {
                            send('move', { x: desk.point.x, y: desk.point.y, zone: desk.zone });
                        }
                        return;
                    }
                }
            }
            send('stop');
        }
    };
    private onCancel = () => { if (this.gesture.cancel().kind === 'release' && LOCAL.controls !== 'tap') send('stop'); this.drawStick(null); };
    private drawStick(anchor: { x: number; y: number } | null, knob?: { x: number; y: number }) {
        const art = this.stickArt;
        if (!art) return;
        art.clear();
        if (!anchor || !knob) return;
        const dx = knob.x - anchor.x, dy = knob.y - anchor.y, d = Math.hypot(dx, dy), r = 44, k = d > r ? r / d : 1;
        art.lineStyle(3, 0xffffff, 0.75).beginFill(0x10213f, 0.18).drawCircle(anchor.x, anchor.y, r).endFill();
        art.lineStyle(0).beginFill(0xffffff, 0.85).drawCircle(anchor.x + dx * k, anchor.y + dy * k, 16).endFill();
    }
    private onTap = (event: any) => {
        const g = LOCAL.model;
        if (LOCAL.modalOpen || !g) return;
        const vp = g.getSystem(PixiViewportSystem).viewport, p = vp.toWorld(event.global.x, event.global.y), target = unprojectWorld(p.x, p.y);
        // No silent rejection: a tap the captain cannot walk to shows a brief red ×.
        if (!walkableAirportPoint(target, tier(state(g)).maxX, gateLayout(tier(state(g)).code).OUTLINE)) { this.invalidTap = { ...target, until: performance.now() + 600 }; return; }
        const selected = ids(g, AirportZone).find(id => {
            if (!this.zones.get(id)?.pad.visible || !zoneAvailable(g, id)) return false;
            const at = g.getTypedUnsafe(Transform, id);
            return Math.abs(target.x - at.x) <= ACTION_PAD.w / 2 && Math.abs(target.y - at.y) <= ACTION_PAD.h / 2;
        });
        if (selected !== undefined) { this.visitZone(selected); return; }
        send('move', target);
    };
    run = (g: ReadOnlyGameModel) => {
        LOCAL.model = g;
        // Moving to an airport with a different floor plan redraws the whole terminal from that plan.
        if (this.world && this.builtLayout !== activeLayout()) this.cleanup(g);
        if (!this.world && !this.initScene(g))
            return;
        const root = this.world!, engine = g.getSystem(PixiViewportSystem), e = state(g), ss = stations(g);
        const captain = localPlayer(g), objective = captain < 0 ? null : objectiveAction(g,captain);
        this.objective = objective;
        if (LOCAL.guidanceFocus && captain >= 0 && objective) {
            const target = guidanceTarget(g, captain, objective);
            if (!target || distance(target, LOCAL.guidanceFocus.point) > 1) LOCAL.guidanceFocus = null;
        }
        for (const sign of this.flightSigns) sign.node.visible = airlineMode(e);
        const plan = gatePlanKey(tier(e).code);
        if (this.viewQuarter !== cameraQuarter() || this.boardGateCount !== ss[6].lanes || this.plan !== plan) {
            const bought = this.plan === plan && this.viewQuarter === cameraQuarter() && this.boardGateCount >= 0 && ss[6].lanes > this.boardGateCount ? this.boardGateCount : -1;
            this.viewQuarter = cameraQuarter();
            this.boardGateCount = ss[6].lanes;
            if (this.plan !== plan) { this.plan = plan; drawGateFloor(this.gateFloor!, gateLayout(tier(e).code).GATES, standReach(tier(e).code)); }
            this.opening?.finish();
            this.turnView(ss[6].lanes, tier(e).code);
            if (bought >= 0) this.openExpansions(g, tier(e).code, bought, ss[6].lanes, captain);
        }
        if (this.opening?.running && !this.opening.step(g.frame) && this.openingFocus && LOCAL.guidanceFocus === this.openingFocus) LOCAL.guidanceFocus = null;
        this.hud!.resize(engine.pixiApp.renderer.width, engine.pixiApp.renderer.height);
        this.hud!.update(g);
        if (e.chairLevel !== this.chairLevel) {
            this.chairLevel = e.chairLevel;
            this.chairs.forEach(chair => chair.destroy());
            this.chairs = [];
            if (e.chairLevel) for (const [i, seat] of EXTRA_SEATS.entries()) {
                const chair = detail(i % 2 ? 7 : 6, 48);
                root.addChild(chair);
                standAt(chair, seat.sortX, seat.sortY);
                this.chairs.push(chair);
            }
        }
        if (e.loaded && shiftsActive(e) && this.lastArrivals !== e.loadFrame && !LOCAL.modalOpen && diaryLines(g).length && !(career(g).offlineAward > 0 && !career(g).offlineClaimed)) {
            this.lastArrivals = e.loadFrame;
            this.hud?.modal.open('arrivals');
        }
        if (e.loaded && career(g).offlineAward > 0 && !career(g).offlineClaimed && this.lastOffline !== e.loadFrame && !LOCAL.modalOpen) {
            this.lastOffline = e.loadFrame;
            this.hud!.modal.open('offline');
        }
        // Build reveals: anticipation-free pop (scale 0.7 → 1.08 → 1) with a dust ring and a 2-frame white flash.
        this.reveals = this.reveals.filter(r => {
            const age = g.frame - r.at;
            if (age > 16) {
                r.art.root.scale.set(1);
                r.flash.destroy();
                return false;
            }
            const p = age / 16;
            r.art.root.scale.set(p < 0.45 ? 0.7 + (p / 0.45) * 0.38 : 1.08 - ((p - 0.45) / 0.55) * 0.08);
            r.flash.clear().lineStyle(3, 0xd9c9a8, 0.55 * (1 - p)).drawCircle(0, 0, 18 + age * 3.4).endFill();
            if (age < 3) r.flash.beginFill(0xffffff, 0.35 - age * 0.12).drawCircle(0, 0, 46).endFill();
            return true;
        });
        for (const id of ids(g, AirportStation)) {
            const s = g.getTypedUnsafe(AirportStation, id);
            let art = this.scenery.get(id);
            if (!art) {
                art = { ...stationArt(s.index), posts: [], postLevel: -1 };
                root.addChild(art.root);
                this.scenery.set(id, art);
                art.root.eventMode = 'static';
                art.root.cursor = 'pointer';
                art.root.interactiveChildren = false;
                art.root.hitArea = art.hitArea();
                art.root.on('pointerdown', event => { event.stopPropagation(); if (LOCAL.modalOpen)
                    return; travel(zoneId(g, s.unlocked ? 'work' : 'build', s.index)); });
            }
            art.open.visible = s.unlocked;
            art.closed.visible = !s.unlocked;
            const seenBuilt = this.revealed.get(id);
            if (seenBuilt === undefined) this.revealed.set(id, s.unlocked);
            else if (seenBuilt !== s.unlocked) {
                this.revealed.set(id, s.unlocked);
                if (s.unlocked && !LOCAL.reducedMotion) {
                    const flash = new Graphics();
                    art.root.addChild(flash);
                    this.reveals.push({ art, flash, at: g.frame });
                }
            }
            if (s.index === 6) setText(art.title, 'GATE A');
            const station = CONFIG.stages[s.index];
            standAt(art.root, station.x, station.y);
            // Only the painted asset accepts a desk click; the surrounding floor stays walkable.
            if (s.unlocked) art.root.hitArea = art.hitArea();
            else {
                const box = art.closed.getBounds(), local = art.root.toLocal({ x: box.x, y: box.y });
                art.root.hitArea = new Rectangle(local.x, local.y, box.width, box.height);
            }
            art.signal.clear();
            if (s.unlocked) {
                const color = s.busy && s.activeWorkers ? C.teal : s.queueLength ? C.gold : 0x8ca9ae;
                art.signal.beginFill(color, s.busy && !LOCAL.reducedMotion ? 0.65 + Math.sin(g.frame * 0.12) * 0.25 : 0.9).drawCircle(0, -8, 3).endFill();
            }
            const allowed = stationAllowed(e, s.index);
            art.root.visible = !outsideTerminal(tier(e), CONFIG.stages[s.index]) && (s.unlocked || !LOCAL.focusedProgression || allowed && disclosePad(e, ss, 'build', s.index));
            const captainWorking = captain >= 0 && onPad('work', g.getTypedUnsafe(Transform, captain), stationPad(s.index, 'work'));
            art.counter.alpha = s.index === 2 && captainWorking ? 0.35 : 1;
            if (s.index === 2) this.scanners.forEach(node => { node.alpha = captainWorking ? 0.3 : 1; });
            art.root.alpha = allowed ? 1 : 0.45;
            setText(art.closedNote, allowed ? (s.invested ? `BUILDING · ${fmt(s.invested)} / ${fmt(unlockCost(s.index, e))}` : 'READY TO BUILD') : `OPENS AT ${(AIRPORTS.find(a => a.stations.includes(s.index))?.name ?? 'A BIGGER AIRPORT').toUpperCase()}`);
            const postLevel = s.unlocked ? s.dividerLevel + cameraQuarter() * 10 : -1;
            if (art.postLevel !== postLevel) {
                art.postLevel = postLevel;
                art.posts.forEach(post => post.destroy({ children: true }));
                art.posts = s.unlocked ? queueRopes(s.index, s.dividerLevel) : [];
                for (const post of art.posts) root.addChild(post);
            }
            const lounging = s.index === 6 ? ids(g, AirportPassenger).filter(id => { const p = g.getTypedUnsafe(AirportPassenger, id); return p.stage === 6 && p.state === 6; }).length : 0;
            art.title.visible = art.activity.visible = !s.activeWorkers;
            setText(art.activity, lounging ? `${lounging} IN LOUNGE` : s.unlocked && s.queueLength ? `${s.queueLength} WAITING` : '');
            art.progress.clear();
            if (s.unlocked && s.busy && s.activeWorkers) {
                art.progress.beginFill(0x17304f, 0.4).drawRoundedRect(-22, 0, 44, 2, 1).endFill();
                const active = ids(g, AirportPassenger).filter(p => { const d = g.getTypedUnsafe(AirportPassenger, p); return d.stage === s.index && d.state === 2; });
                if (active.length) {
                    const p = g.getTypedUnsafe(AirportPassenger, active[0]);
                    const pct = Math.max(0, Math.min(1, 1 - p.remaining / serviceFrames(s.index, s, p.serial)));
                    art.progress.beginFill(CONFIG.stages[s.index].color).drawRoundedRect(-22, 0, Math.max(1, pct * 44), 2, 1).endFill();
                }
            }
        }
        for (const id of ids(g, AirportZone)) {
            const z = g.getTypedUnsafe(AirportZone, id), p = g.getTypedUnsafe(Transform, id);
            let art = this.zones.get(id);
            if (!art) {
                // The pad is painted on the floor; its lettering stands upright above it.
                const pad = new Container();
                pad.position.set(p.x, p.y);
                this.ground!.addChild(pad);
                const c = new Container();
                root.addChild(c);
                const circle = shape(pad), title = label(c, '', 0, -12, 11, C.ink, '800'), price = label(c, '', 0, 14, 11, C.ink, '700');
                // The pad speaks through one pictograph and, when it costs something, a price.
                for (const text of [price]) Object.assign(text.style, { stroke: 0xffffff, strokeThickness: 3.5, lineJoin: 'round' });
                title.anchor.set(0.5);
                price.anchor.set(0.5);
                const icon = bills(c, 0, 12, 6);
                icon.visible = false;
                pad.eventMode = 'static';
                pad.cursor = 'pointer';
                // Keep a forgiving tap margin around the painted work circle.
                const size = ACTION_PAD;
                pad.hitArea = new RoundedRectangle(-size.w / 2, -size.h / 2, size.w, size.h, 8);
                pad.on('pointerdown', ev => { ev.stopPropagation(); this.visitZone(id); });
                art = { root: c, pad, circle, title, price, icon };
                this.zones.set(id, art);
            }
            const carryingTrash = captain >= 0 && ids(g, AirportTrash).some(t => g.getTypedUnsafe(AirportTrash, t).carrier === captain);
            const carryingBags = captain >= 0 && ids(g, AirportBag).some(b => g.getTypedUnsafe(AirportBag, b).carrier === captain);
            const s = ss[z.stage];
            const near = captain >= 0 && distance(g.getTypedUnsafe(Transform, captain), p) < z.radius;
            const selected = captain >= 0 && g.getTypedUnsafe(AirportAgent, captain).intent === id;
            const management = ['hire', 'upgrade', 'divider', 'boots', 'bags', 'cleaning', 'shuttle', 'chairs', 'vending', 'puzzle'].includes(z.kind);
            const floorPad = z.kind === 'upgrade' && disclosePad(e, ss, 'upgrade', z.stage);
            const shown = (!management || floorPad) && zoneAvailable(g, id) && (floorPad || !LOCAL.focusedProgression || selected || disclosePad(e,ss,z.kind as PadKind,z.stage));
            art.root.visible = art.pad.visible = shown;
            if (!art.root.visible)
                continue;
            // Pad lettering is interaction UI: it floats over walls and props, and fades while you stand on it.
            standAt(art.root, p.x, p.y, 2000000);
            const color = z.kind === 'work' ? (z.stage >= 0 ? CONFIG.stages[z.stage].color : C.blue) : z.kind === 'build' ? C.gold : z.kind === 'hire' ? 0xb798db : z.kind === 'upgrade' ? C.teal : z.kind === 'belt' ? C.gold : C.teal;
            art.root.alpha = near ? 0.75 : 1;
            const urgent = z.kind === 'work' && z.stage >= 0 && s.queueLength > 0 && !s.activeWorkers
                || z.kind === 'trash' && carryingTrash
                || z.kind === 'belt' && carryingBags
                || z.kind === 'vending' && vendingReady(vending(g)[z.stage]);
            const pulse = urgent && !LOCAL.reducedMotion ? 0.45 + 0.5 * Math.abs(Math.sin(g.frame * 0.09)) : 0.85;
            const padSize = z.kind === 'work' ? WORK_PAD : ACTION_PAD;
            art.circle.clear().lineStyle(2.5, color, pulse).beginFill(color, near ? 0.32 : urgent ? 0.2 : 0.12)
                .drawRoundedRect(-padSize.w / 2, -padSize.h / 2, padSize.w, padSize.h, 8).endFill();
            // A build pad in progress fills gold with the pour (invested / cost) instead of text-only feedback.
            if (z.kind === 'build' && s.invested > 0) {
                const frac = Math.min(1, s.invested / unlockCost(z.stage, e));
                art.circle.beginFill(C.gold, 0.3).drawRoundedRect(-padSize.w / 2 + 2, -padSize.h / 2 + 2, Math.max(6, (padSize.w - 4) * frac), padSize.h - 4, 6).endFill();
            }
            // A speed pad fills as the captain stands on it, then buys.
            if (floorPad && near && captain >= 0 && s.upgradeUntil <= g.frame && Number.isFinite(upgradeCost(s, 'speed', e)) && career(g).money >= upgradeCost(s, 'speed', e)) {
                const frac = Math.min(1, g.getTypedUnsafe(AirportAgent, captain).dwell / FLOOR_UPGRADE_DWELL);
                art.circle.beginFill(C.teal, 0.35).drawRoundedRect(-padSize.w / 2 + 2, -padSize.h / 2 + 2, Math.max(6, (padSize.w - 4) * frac), padSize.h - 4, 6).endFill();
            }
            let price = '', cost = -1;
            if (z.kind === 'build') { cost = unlockCost(z.stage, e) - s.invested; price = fmt(cost); }
            else if (z.kind === 'hire') { cost = managerCost(z.stage, e, e.hires); price = fmt(cost); }
            else if (z.kind === 'upgrade') {
                const c = upgradeCost(s, 'speed', e);
                if (s.upgradeUntil > g.frame) price = `${Math.ceil((s.upgradeUntil - g.frame) / 60)}s`;
                else if (Number.isFinite(c)) { cost = c; price = fmt(c); } else price = 'MAX';
            }
            else if (z.kind === 'vending') {
                const v = vending(g)[z.stage], c = Math.min(vendingCost(v, 'speed'), vendingCost(v, 'income'));
                if (!vendingReady(v)) price = 'MORE SALES';
                else if (v.upgradeUntil > g.frame) price = `${Math.ceil((v.upgradeUntil - g.frame) / 60)}s`;
                else if (Number.isFinite(c)) { cost = c; price = fmt(c); } else price = 'MAX';
            }
            else if (z.kind === 'divider' || z.kind in SPECIAL_TIMERS) {
                const q = equipmentQuote(e, ss, z.kind as EquipmentKind, z.stage);
                if (q) {
                    if (q.until > g.frame) price = `${Math.ceil((q.until - g.frame) / 60)}s`;
                    else if (Number.isFinite(q.cost)) { cost = q.cost; price = fmt(q.cost); } else price = 'MAX';
                }
            }
            setText(art.title, z.kind === 'trash' ? '' : ZONE_GLYPH[z.kind] ?? '📍');
            setText(art.price, price);
            art.title.visible = true;
            art.price.visible = !near;
            art.title.alpha = urgent ? 1 : 0.8;
            // Costs use bill icons and turn muted red when the wallet cannot cover them yet.
            art.icon.visible = cost >= 0 && !near;
            art.price.x = cost >= 0 ? 7 : 0;
            art.icon.x = -art.price.width / 2 - 3;
            art.price.style.fill = cost >= 0 && cost > career(g).money ? 0xb4534f : C.ink;
        }
        for (let gate = 1; gate < ss[6].lanes; gate++) {
            let desk = this.gateDesks.get(gate);
            if (!desk) {
                desk = stationArt(6, gatePose(tier(e).code, gate).facing); this.gateDesks.set(gate, desk); root.addChild(desk.root);
                desk.root.eventMode = 'static'; desk.root.cursor = 'pointer'; desk.root.hitArea = desk.hitArea();
                desk.root.on('pointerdown', event => { event.stopPropagation(); const zone = ids(g, AirportZone).find(id => { const z = g.getTypedUnsafe(AirportZone, id); return z.stage === 6 && z.kind === 'work' && z.gate === gate; }); if (zone !== undefined) travel(zone); });
            }
            desk.open.visible = true; desk.closed.visible = false;
            desk.root.visible = ss[6].unlocked;
            setText(desk.title, `GATE ${String.fromCharCode(65 + gate)}`);
            const pose = gatePose(tier(e).code, gate);
            standAt(desk.root, pose.x, pose.y);
        }
        for (const [gate, desk] of this.gateDesks) if (gate >= ss[6].lanes) desk.root.visible = false;
        const all = [...ids(g, AirportAgent), ...ids(g, AirportPassenger), ...ids(g, AirportStaff)];
        this.bubbleBudget.clear();
        this.entrance!.update(all.map(id => g.getTypedUnsafe(Transform, id)), LOCAL.reducedMotion);
        const active = new Set(all);
        for (const [id, art] of this.people)
            if (!active.has(id)) {
                art.container.destroy({ children: true });
                this.people.delete(id);
            }
        for (const id of all) {
            const captain = g.hasComponent(AirportAgent, id), staff = g.getTyped(AirportStaff, id), passenger = g.getTyped(AirportPassenger, id), agent = g.getTyped(AirportAgent, id);
            let art: PersonArt | undefined = this.people.get(id);
            if (!art) {
                // MGT-012 visible tells: business travellers wear dark suits; families and international travellers carry a tag.
                const kind = passenger ? archetype(passenger.serial, passenger.vip, shiftsActive(e)) : null;
                const intl = !!passenger && shiftsActive(e) && internationalTraveller(passenger.serial, ss);
                const color = captain ? [0x4089bd, 0xa677c5, 0xce9160, 0x5da78b][agent!.color % 4] : staff ? 0xe7bd60 : kind === 'business' ? 0x2f3b55 : SHIRTS[(passenger?.tint ?? 0) % SHIRTS.length];
                const personArt = person(color, captain, !!staff, id, staff?.role);
                root.addChild(personArt.container);
                const name = label(personArt.container, captain ? 'CAPTAIN' : staff?.role === 'porter' ? 'PORTER' : staff?.role === 'pilot' ? 'PILOT' : staff?.role === 'clerk' ? 'CLERK' : staff?.role === 'cleaner' ? 'CLEANER' : staff?.role === 'runner' ? 'RUNNER' : kind === 'business' ? 'BUSINESS' : kind === 'family' ? 'FAMILY' : kind === 'pet' ? 'PET' : intl ? 'INTL' : '', 0, -65, 9, C.navy, '800');
                name.anchor.set(0.5);
                const stack = new Container();
                personArt.container.addChild(stack);
                const question = new Container();
                rounded(question, -13, -87, 26, 25, 7, C.white);
                shape(question).beginFill(C.white).drawPolygon([-4, -62, 4, -62, 0, -57]).endFill();
                label(question, '?', 0, -75, 18, C.navy, '800').anchor.set(0.5);
                personArt.container.addChild(question);
                // Mood bubbles speak the same language as the '?' bubble: white pill, one bold glyph.
                const bubble = new Container();
                rounded(bubble, -11, -88, 22, 19, 6, C.white);
                shape(bubble).beginFill(C.white).drawPolygon([-3.5, -66.5, 3.5, -66.5, 0, -62]).endFill();
                const sadFace = new Graphics();
                sadFace.beginFill(0x4a90d9).drawPolygon([0, -84, -4.2, -77.5, 4.2, -77.5]).drawCircle(0, -76.5, 4.2).endFill();
                bubble.addChild(sadFace);
                const angryFace = label(bubble, '!', 0, -78, 15, 0xb4534f, '800');
                angryFace.anchor.set(0.5);
                personArt.container.addChild(bubble);
                art = { ...personArt, name, question, stack, bags: -1, litter: -1, facingX: 1, facingY: 1, bubble, sadFace, angryFace, mood: -1, follow: followIdle(), sway: 0, swayV: 0, lastVX: 0, lastVY: 0, stackBaseX: [] };
                this.people.set(id, art);
            }
            const t = g.getTypedUnsafe(Transform, id), v = g.getTypedUnsafe(Locomotion, id);
            // Inside a jet bridge's tunnel (past the wall going out, or not yet back to it coming in), travellers are out of sight.
            const inBridge = !!passenger && passenger.gate >= 0 && boardingStyle(tier(e).code) === 'bridge' && (passenger.arriving ? passenger.walkStep < 0 : passenger.state === 3 && passenger.walkStep >= 3);
            art.container.visible = !inBridge && !(passenger?.arriving && g.frame < passenger.releaseFrame) && (g.getTyped(AirportPilot, id)?.flyingGate ?? -1) < 0;
            // The captain sprite follows the sim position with an attack/release envelope (render-only).
            if (captain && !LOCAL.reducedMotion) {
                art.follow = stepFollow(art.follow, v.x, v.y);
                standAt(art.container, t.x + art.follow.ox, t.y + art.follow.oy);
            }
            else {
                if (captain) art.follow = followIdle();
                standAt(art.container, t.x, t.y);
            }
            art.question.visible = passenger?.state === 7 && distance(t, inquiryPoint()) < 8;
            const mood = passenger && !passenger.arriving ? (passenger.happiness < 40 ? 2 : passenger.patience <= ANGRY_PATIENCE || passenger.happiness < 70 ? 1 : 0) : 0;
            const sadNearby = !!passenger && passenger.state === 0 && passenger.sad;
            if (mood !== art.mood) {
                art.mood = mood;
                art.sadFace.visible = mood === 0;
                art.angryFace.visible = mood > 0;
            }
            // Cap the chorus: at most three mood bubbles per queue keep the floor readable.
            let show = (mood > 0 || sadNearby) && !art.question.visible;
            if (show && passenger) {
                const used = this.bubbleBudget.get(passenger.stage) ?? 0;
                if (used >= 3) show = false;
                else this.bubbleBudget.set(passenger.stage, used + 1);
            }
            art.bubble.visible = show;
            const walking = Math.abs(v.x) + Math.abs(v.y) > 0.1;
            const seat = passenger?.seated ? waitingSeats(e.chairLevel, tier(e).maxX)[passenger.seat] : undefined;
            if (seat) {
                standAt(art.container, seat.x, seat.y);
                // A seat belongs above the sofa back even when its floor centre sorts behind it.
                art.container.zIndex = projectWorld(seat.sortX, seat.sortY).y + 2;
            }
            // The renders animate themselves; a seated figure's hips sit on the cushion, not on the floor.
            art.body.y = seat ? SEAT_LIFT : 0;
            // Up (or down) the airstairs: lifted along the flight and drawn over it.
            const climb = passenger && passenger.gate >= 0 && !seat ? stairsProgress(tier(e).code, passenger.gate, t) : 0;
            if (climb > 0) {
                const top = aircraftDoor(tier(e).code, passenger!.gate);
                art.body.y = -(4 + (DOOR_SILL - 4) * climb);
                art.container.zIndex = projectWorld((top.foot.x + top.at.x) / 2, (top.foot.y + top.at.y) / 2).y + 1;
            }
            if (walking) { art.facingX = v.x; art.facingY = v.y; }
            if (!walking) {
                // Standing still, people face what they are there for rather than where they came from.
                const facing = restingFacing(t, passenger, staff, captain, tier(e).code);
                if (facing) { art.facingX = facing.x; art.facingY = facing.y; }
            }
            if (seat) { art.facingX = 0; art.facingY = seat.facingY; }
            const speed = Math.hypot(v.x, v.y);
            const action = seat ? 'sit' : !walking ? 'idle' : agent?.entering || speed >= 4.4 ? 'run' : 'walk';
            posePerson(art.image, art.cell, action, art.facingX, art.facingY, LOCAL.reducedMotion ? 0 : peopleAnimationTick(action, g.frame, id * 7));
            if (captain) {
                setText(art.name, id === localPlayer(g) ? 'YOU' : 'CAPTAIN ' + (agent!.color + 1));
                art.name.visible = ids(g, AirportAgent).length > 1 && !CONFIG.stages.some((_, i) => onPad('work', t, stationPad(i, 'work')));
            }
            const count = ids(g, AirportBag).filter(b => g.getTypedUnsafe(AirportBag, b).carrier === id).length;
            const litterCount = ids(g, AirportTrash).filter(t => g.getTypedUnsafe(AirportTrash, t).carrier === id).length;
            if (count !== art.bags || litterCount !== art.litter) {
                art.litter = litterCount;
                art.bags = count;
                art.stack.removeChildren().forEach(c => c.destroy({ children: true }));
                art.stackBaseX = [];
                for (let i = 0; i < Math.min(12, count); i++) {
                    suitcase(art.stack, 0, -62 - i * 11, [C.gold, 0x779dbe, 0xb296d3][i % 3], 0.66);
                    art.stackBaseX.push(0);
                }
                for (let i = 0; i < litterCount; i++) {
                    trashArt(art.stack, -24, -55 - i * 10, i % 3);
                    art.stackBaseX.push(-24);
                }
                art.name.y = -65 - Math.max(count, litterCount) * 11;
            }
            // The carried stack leans against acceleration with a damped spring and recovers at rest.
            const accelX = projectDirection(v.x - art.lastVX, v.y - art.lastVY).x;
            art.lastVX = v.x;
            art.lastVY = v.y;
            const lean = stepSway(art.sway, art.swayV, Math.max(-6, Math.min(6, -accelX * 2.2)));
            art.sway = lean.sway;
            art.swayV = lean.swayV;
            if (art.stack.children.length) {
                const spread = (LOCAL.reducedMotion ? 0.25 : 1) / art.stack.children.length;
                art.stack.children.forEach((child, i) => { child.x = (art.stackBaseX[i] ?? 0) + art.sway * (i + 1) * spread; });
            }
        }
        const bagIds = new Set(ids(g, AirportBag));
        for (const [id, c] of this.parcels)
            if (!bagIds.has(id)) {
                c.destroy({ children: true });
                this.parcels.delete(id);
            }
        for (const id of bagIds) {
            const b = g.getTypedUnsafe(AirportBag, id), p = g.getTypedUnsafe(Transform, id);
            let c = this.parcels.get(id);
            if (!c) {
                c = suitcase(root, p.x, p.y, [C.gold, 0x82abc4, 0xbe91b3][b.serial % 3], 0.85);
                this.parcels.set(id, c);
            }
            c.visible = b.carrier < 0;
            standAt(c, p.x, p.y);
        }
        const activePlanes = new Set(ids(g, AirportPlane));
        for (const [id, c] of this.planes) if (!activePlanes.has(id)) { c.root.destroy({ children: true }); this.planes.delete(id); }
        for (const [id, stairs] of this.airstairs) if (!activePlanes.has(id) || boardingStyle(tier(e).code) !== 'stairs') { stairs.destroy({ children: true }); this.airstairs.delete(id); }
        const airline = airlineMode(e), port = tier(e);
        this.gateBoards!.update(g, this.hud!.worldOcclusion());
        if (this.fenceTier !== `${port.maxX}:${gatePlanKey(port.code)}`) {
            this.fenceTier = `${port.maxX}:${gatePlanKey(port.code)}`;
            this.fence!.clear();
            // An airport with its own outline: the terminal floor is clipped to it and outside is open ground.
            const outline = gateLayout(port.code).OUTLINE;
            this.floorClip!.clear();
            if (outline) this.floorClip!.beginFill(0xffffff).drawPolygon([...outline]).endFill();
            else this.floorClip!.beginFill(0xffffff).drawRect(-4000, -4000, CONFIG.width + 8000, CONFIG.height + 8000).endFill();
            if (outline) for (let x = 120; x < CONFIG.width; x += 200) for (let y = 470; y < CONFIG.height - 40; y += 210)
                if (!insidePolygon(outline, x + (y % 3) * 25, y)) this.fence!.beginFill(0x47ada6, 0.8).drawEllipse(x + (y % 3) * 25, y, 46, 30).endFill();
            if (port.maxX < CONFIG.width) {
                this.fence!.beginFill(0x75c8d6, 0.92).drawRect(port.maxX, 330, CONFIG.width - port.maxX + 60, CONFIG.height - 300).endFill();
                this.fence!.lineStyle(6, 0x3c90ba).moveTo(port.maxX, 345).lineTo(port.maxX, CONFIG.height - 30);
                for (let y = 360; y < CONFIG.height - 30; y += 40) this.fence!.lineStyle(3, 0x3c90ba).moveTo(port.maxX, y).lineTo(port.maxX + 14, y + 14);
            }
            this.fenceText!.visible = port.maxX < CONFIG.width;
            standAt(this.fenceText!.parent!, port.maxX + 300, 1200, 900000);
            for (const item of this.standing) item.node.alpha = outsideTerminal(port, item) ? 0 : item.x > port.maxX ? 0.35 : 1;
        }
        for (const id of activePlanes) {
            let c = this.planes.get(id);
            if (!c) {
                c = planeArt();
                root.addChild(c.root);
                this.planes.set(id, c);
            }
            const p = g.getTypedUnsafe(Transform, id);
            const plane = g.getTypedUnsafe(AirportPlane, id);
            // Nose along the way it is moving; parked, as parkedHeading lays it out at its gate.
            const last = this.planeTrail.get(id), dx = last ? p.x - last.x : 0, dy = last ? p.y - last.y : 0;
            this.planeTrail.set(id, { x: p.x, y: p.y });
            const moving = Math.hypot(dx, dy) > 0.05;
            const parked = parkedHeading(tier(e).code, Math.min(plane.gate, gateLayout(tier(e).code).GATES.length - 1)).nose;
            faceAircraft(c.image, moving ? dx : parked[0], moving ? dy : parked[1]);
            standAt(c.root, p.x, p.y);
            c.root.y += deckDrop(tier(e).code);
            const deck = deckDrop(tier(e).code) ? gateLayout(tier(e).code).OUTLINE : undefined, layer = deck && behindDeck(deck, p.x, p.y) ? this.behind! : root;
            if (c.root.parent !== layer) layer.addChild(c.root);
            c.image.y = plane.phase === PHASE.TAXI || plane.phase === PHASE.APPROACH ? -groundPose(tier(e).code, plane.gate, plane.clock, plane.phase === PHASE.APPROACH).lift : 0;
            c.root.visible = plane.phase !== PHASE.AIRBORNE;
            if (boardingStyle(tier(e).code) === 'stairs') {
                let stairs = this.airstairs.get(id);
                if (!stairs) { stairs = airstairs(tier(e).code, plane.gate); root.addChild(stairs); this.airstairs.set(id, stairs); }
                // Only against a parked aircraft; the sprite follows the camera quarter by itself.
                stairs.visible = distance(p, aircraftStand(tier(e).code, plane.gate)) < 1;
                const { at: top, foot } = aircraftDoor(tier(e).code, plane.gate);
                standAt(stairs, (top.x + foot.x) / 2, (top.y + foot.y) / 2);
            }
        }
        for (const id of ids(g, AirportCash)) {
            const ci = g.getTypedUnsafe(AirportCash, id), s = ci.vending >= 0 ? vending(g)[ci.vending] : ss[ci.stage], p = g.getTypedUnsafe(Transform, id);
            let art = this.cash.get(id);
            if (!art) {
                const c = new Container();
                root.addChild(c);
                const pile = new Container();
                c.addChild(pile);
                const chip = label(c, '', 0, -30, 11, C.navy, '800');
                chip.anchor.set(0.5);
                Object.assign(chip.style, { stroke: 0xffffff, strokeThickness: 3.5, lineJoin: 'round' });
                art = { root: c, pile, chip, bracket: -1 };
                this.cash.set(id, art);
                c.eventMode = 'static';
                c.hitArea = new Rectangle(-30, -24, 60, 62);
                c.cursor = 'pointer';
                c.on('pointerdown', ev => { ev.stopPropagation(); if (!LOCAL.modalOpen)
                    send('move', { x: p.x, y: p.y }); });
            }
            // Piles grow in discrete brackets so a waiting reward is visible from across the floor.
            const bracket = pileLayers(s.pending);
            if (bracket !== art.bracket) {
                art.bracket = bracket;
                art.pile.removeChildren().forEach(child => child.destroy({ children: true }));
                for (let i = 0; i < bracket; i++) {
                    const bundle = detail(16, 44);
                    bundle.y = 6 - i * 5;
                    bundle.x = PILE_X[i];
                    art.pile.addChild(bundle);
                }
            }
            art.root.visible = s.pending > 0;
            art.chip.visible = s.pending > 99;
            setText(art.chip, fmt(s.pending));
            standAt(art.root, p.x, p.y, 10);
        }
        const activeTrash = new Set(ids(g, AirportTrash));
        const trashPts = [...activeTrash].map(tid => g.getTypedUnsafe(Transform, tid));
        for (const [id, art] of this.litter) if (!activeTrash.has(id)) { art.root.destroy({ children: true }); this.litter.delete(id); }
        for (const id of activeTrash) {
            const t = g.getTypedUnsafe(AirportTrash, id), p = g.getTypedUnsafe(Transform, id);
            let art = this.litter.get(id);
            if (!art) {
                const rootArt = trashArt(root, p.x, p.y, t.kind);
                const stink = new Graphics();
                stink.lineStyle(2, 0x7fae6a, 1);
                for (const off of [-7, 5]) {
                    stink.moveTo(off, -8);
                    stink.bezierCurveTo(off - 4, -16, off + 4, -22, off, -30);
                }
                rootArt.addChild(stink);
                art = { root: rootArt, stink };
                this.litter.set(id, art);
                const hint = label(rootArt, 'LITTER · PICK UP', 0, -26, 9, C.navy, '800');
                hint.anchor.set(0.5);
                Object.assign(hint.style, { stroke: C.white, strokeThickness: 3 });
                rootArt.eventMode = 'static'; rootArt.cursor = 'pointer'; rootArt.hitArea = new Rectangle(-22, -22, 44, 44);
                rootArt.on('pointerdown', ev => { ev.stopPropagation(); if (!LOCAL.modalOpen) send('move', { x: p.x, y: p.y }); });
            }
            standAt(art.root, p.x, p.y); art.root.visible = t.carrier < 0;
            // Stink squiggles fade in with pile density; cleaning clears them the same frame.
            let nearCount = 0;
            for (const q of trashPts) {
                const dx = q.x - p.x, dy = q.y - p.y;
                if (dx * dx + dy * dy < SAD_RADIUS * SAD_RADIUS) nearCount++;
            }
            art.stink.visible = nearCount >= 2;
            art.stink.alpha = Math.min(0.6, 0.15 * nearCount);
        }
        for (const id of ids(g, AirportVending)) {
            const v = g.getTypedUnsafe(AirportVending, id), p = g.getTypedUnsafe(Transform, id);
            let art = this.machines.get(id);
            if (!art) {
                art = vendingArt(v.index, VENDING[v.index].color);
                root.addChild(art.root); this.machines.set(id, art);
                art.root.eventMode = 'static'; art.root.cursor = 'pointer'; art.root.hitArea = art.hitArea();
                art.root.on('pointerdown', ev => { ev.stopPropagation(); if (!LOCAL.modalOpen) this.visitZone(zoneId(g, 'vending', v.index)); });
            }
            standAt(art.root, p.x, p.y);
            art.root.hitArea = art.hitArea();
            const open = port.vending.includes(v.index);
            art.root.alpha = open ? 1 : 0.35;
            setText(art.status, !open ? 'BIGGER AIRPORT' : vendingReady(vending(g)[v.index]) ? 'UPGRADE READY' : '');
        }
        this.collision!.clear();
        if (LOCAL.debugCollision) {
            this.collision!.lineStyle(2, 0xff3858, 0.85);
            for (const wall of ids(g, RigidBox)) {
                const r = g.getTypedUnsafe(RigidBox, wall), p = g.getTypedUnsafe(Transform, wall);
                const angle = r.angle * Math.PI / 180;
                const corners = [[-r.width/2,-r.height/2],[r.width/2,-r.height/2],[r.width/2,r.height/2],[-r.width/2,r.height/2]];
                this.collision!.drawPolygon(corners.flatMap(([x,y]) => [p.x+x*Math.cos(angle)-y*Math.sin(angle),p.y+x*Math.sin(angle)+y*Math.cos(angle)]));
            }
            for (const id of all) { const p = g.getTypedUnsafe(Transform, id); this.collision!.drawCircle(p.x, p.y, 15); }
        }
        const pid = localPlayer(g);
        this.marker!.clear();
        if (LOCAL.guidanceFocus && !LOCAL.modalOpen) {
            const { x, y } = LOCAL.guidanceFocus.point, pulse = LOCAL.reducedMotion ? 0 : Math.sin(g.frame * 0.09) * 4;
            this.marker!.lineStyle(4, C.teal, 1).beginFill(C.teal, 0.18).drawCircle(x, y, 32 + pulse).endFill();
            this.marker!.lineStyle(2, C.white, 1).drawCircle(x, y, 23 + pulse);
        }
        if (this.invalidTap && performance.now() < this.invalidTap.until) {
            const { x, y } = this.invalidTap, s = 14;
            this.marker!.lineStyle(6, 0xd94a4a, 0.95).moveTo(x - s, y - s).lineTo(x + s, y + s).moveTo(x + s, y - s).lineTo(x - s, y + s);
        } else this.invalidTap = null;
        // MGT-020: active crises pulse red where a captain can fix them.
        if (shiftsActive(e)) for (const c of activeCrises(e)) {
            const at = crisisPoint(c.crisis.station), pulse = LOCAL.reducedMotion ? 0 : Math.sin(g.frame * 0.15) * 5;
            this.marker!.lineStyle(5, 0xd94a4a, 0.9).drawCircle(at.x, at.y, 34 + pulse).lineStyle(4, 0xd94a4a, 1).moveTo(at.x, at.y - 14).lineTo(at.x, at.y + 4).moveTo(at.x, at.y + 10).lineTo(at.x, at.y + 12);
        }
        if (pid >= 0) {
            const path = g.getTypedUnsafe(AirportPath, pid);
            if (path.moving) {
                const p = g.getTypedUnsafe(Transform, pid);
                this.marker!.lineStyle(2, C.navy, 0.22).moveTo(p.x, p.y);
                for (let i = path.cursor; i < path.points.length; i += 2)
                    this.marker!.lineTo(path.points[i], path.points[i + 1]);
                const tx = path.points[path.points.length - 2], ty = path.points[path.points.length - 1], pulse = LOCAL.reducedMotion ? 0 : Math.sin(g.frame * 0.1) * 3;
                this.marker!.lineStyle(2.5, C.teal, 0.9).drawCircle(tx, ty, 18 + pulse).lineStyle(2, C.white).moveTo(tx - 5, ty).lineTo(tx + 5, ty).moveTo(tx, ty - 5).lineTo(tx, ty + 5);
            }
            // Pizza-style wayfinding: green chevrons march from the captain toward the objective.
            this.arrows!.clear();
            const action = pid >= 0 ? this.objective : null;
            const target = action ? guidanceTarget(g, pid, action) : null;
            this.tutorialCue!.visible = !!target && !LOCAL.modalOpen && LOCAL.guidance === "full";
            if (target && this.tutorialCue!.visible) {
                const at = projectWorld(target.x, target.y);
                this.tutorialCue!.position.set(at.x, at.y - 84 - (LOCAL.reducedMotion ? 0 : Math.sin(g.frame * 0.09) * 4));
                this.tutorialCue!.zIndex = 2000001;
                setText(this.tutorialCue!, guidanceCaption(action!));
                this.arrows!.lineStyle(4, C.teal, 0.9).drawCircle(target.x, target.y, 32);
            }
            // TX-019: after 4 s standing still, a soft glow marks where the current goal is.
            const here = g.getTypedUnsafe(Transform, pid), still = !g.getTypedUnsafe(AirportPath, pid).moving;
            this.idleFrames = still && Math.abs(here.x - this.idleAt.x) + Math.abs(here.y - this.idleAt.y) < 1 ? this.idleFrames + 1 : 0;
            this.idleAt = { x: here.x, y: here.y };
            this.idleCue = !!target && !LOCAL.modalOpen && LOCAL.guidance === 'full' && this.idleFrames >= IDLE_CUE_FRAMES && distance(here, target) > 60;
            if (this.idleCue && target) {
                const glow = LOCAL.reducedMotion ? 0.5 : 0.35 + 0.25 * Math.sin(g.frame * 0.12);
                this.arrows!.lineStyle(0).beginFill(C.gold, glow * 0.35).drawCircle(target.x, target.y, 58).endFill();
                this.arrows!.lineStyle(4, C.gold, glow + 0.3).drawCircle(target.x, target.y, 46 + (LOCAL.reducedMotion ? 0 : Math.sin(g.frame * 0.12) * 5));
            }
            // TX-020: experienced players play with the same rules but without the chevron trail and idle glow.
            if (target && !LOCAL.modalOpen && LOCAL.guidance === 'full')
                for (const [i, c] of chevronTrail(g.getTypedUnsafe(Transform, pid), target, g.frame, LOCAL.reducedMotion ? 0 : 0.9).entries()) {
                    const alpha = 0.55 + (LOCAL.reducedMotion ? 0.3 : 0.3 * Math.sin(g.frame * 0.18 - i * 0.9)), ca = Math.cos(c.angle), sa = Math.sin(c.angle);
                    const px = (ox: number, oy: number) => ({ x: c.x + ox * ca - oy * sa, y: c.y + ox * sa + oy * ca });
                    const tip = px(9, 0), left = px(-7, -10), right = px(-7, 10);
                    this.arrows!.lineStyle(5, C.teal, alpha).moveTo(left.x, left.y).lineTo(tip.x, tip.y).lineTo(right.x, right.y);
                }
            else this.arrows!.clear();
        }
    };
    cleanup = (g: ReadOnlyGameModel) => { LOCAL.guidanceFocus = null; const vpOff = g.getSystem(PixiViewportSystem).viewport; vpOff?.off('pointerdown', this.onDown); vpOff?.off('pointermove', this.onMove); vpOff?.off('pointerup', this.onUp); vpOff?.off('pointerupoutside', this.onUp); vpOff?.off('pointercancel', this.onCancel); this.stickArt?.destroy(); this.stickArt = null; window.removeEventListener('keydown', this.onKey); this.hud?.destroy(); this.entrance?.destroy(); this.entrance = null; // Pieces inside the world go first: destroying the world also destroys its children.
        this.reveals.forEach(r => r.flash.destroy()); this.reveals = []; this.opening?.finish(); this.opening = null; this.openingFocus = null; this.gateBoards?.destroy(); this.gateBoards = null; this.world?.destroy({ children: true }); this.world = null; this.ground = null; this.airfield = null; this.facade = null; this.behind = null; this.floorClip = null; this.floorInside = null; this.hoarded = null; this.tutorialCue = null; this.standing = []; this.wallPieces = []; this.detectors = []; this.scanners = []; this.chairs = []; this.viewQuarter = -1; this.boardGateCount = -1; this.chairLevel = -1; this.people.clear(); this.scenery.clear(); this.gateDesks.clear(); this.revealed.clear(); this.zones.clear(); this.parcels.clear(); this.planes.clear(); this.airstairs.clear(); this.planeTrail.clear(); this.cash.clear(); this.litter.clear(); this.machines.clear(); this.flightSigns = []; this.plan = null; this.fenceTier = ''; };
}

/** True for a point in the cut-away corner of an L-shaped airport (outside its short landside hall). */
function outsideTerminal(port: { code: string }, p: { x: number; y: number }): boolean {
    const outline = gateLayout(port.code).OUTLINE;
    return !!outline && p.y > 345 && !insidePolygon(outline, p.x, p.y);
}
